import { useEffect, useRef, useState } from 'react'
import Image from 'next/future/image'
import { addToHomeData, clickedPromos } from '@smuchile/pkg-unimarc-hooks'
import { BannerHomeObj } from 'shared/interfaces/Home'
import styles from './Worlds.module.css'
const MOBILE_LOOP_IDLE_DELAY_MS = 120

export type ContentfulFields = Record<string, unknown>

export interface NormalizedWorld {
  id: string
  label: string
  image: string
  href?: string
  referenceType?: string
  source: ContentfulFields
}

const asRecord = (value: unknown): ContentfulFields => {
  return typeof value === 'object' && value !== null
    ? (value as ContentfulFields)
    : {}
}

const fieldsOf = (value: unknown): ContentfulFields => {
  const record = asRecord(value)
  return record.fields && typeof record.fields === 'object'
    ? asRecord(record.fields)
    : record
}

const stringValue = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

const normalizeImageUrl = (value: unknown): string | undefined => {
  const url = stringValue(value)
  if (!url) return undefined
  return url.startsWith('//') ? `https:${url}` : url
}

const imageUrl = (value: unknown): string | undefined => {
  const image = fieldsOf(value)
  const file = asRecord(image.file)
  return normalizeImageUrl(file.url) || normalizeImageUrl(image.url)
}

const textOf = (value: unknown): string => {
  return stringValue(fieldsOf(value).label) || ''
}

const selectWorldImage = (
  fields: ContentfulFields,
  isMobile: boolean
): string | undefined => {
  const preferred = fields[isMobile ? 'imageMobile' : 'imageDesktop']
  const fallback = fields[isMobile ? 'imageDesktop' : 'imageMobile']
  return imageUrl(preferred) || imageUrl(fallback)
}

/** Resolve one target according to the existing Contentful reference semantics. */
export const resolveWorldLink = (value: unknown): string | undefined => {
  const fields = fieldsOf(value)
  const viewAll = stringValue(fields.referenceViewAll)
  if (viewAll) return viewAll
  const referenceType = stringValue(fields.referenceType)?.toLowerCase()
  if (referenceType === 'url') {
    return (
      stringValue(fields.reference) ||
      stringValue(fields.url) ||
      stringValue(fields.urlPath)
    )
  }
  if (referenceType === 'category' || referenceType === 'collection') {
    const refFields = fieldsOf(fields.reference)
    const ref = stringValue(fields.reference)
    let navigableRef: string | undefined
    if (ref?.startsWith('/') || ref?.toLowerCase().startsWith('http')) {
      navigableRef = ref
    } else if (ref?.includes('/')) {
      navigableRef = `/${ref}`
    }
    return (
      stringValue(fields.url) ||
      stringValue(refFields.url) ||
      stringValue(fields.urlPath) ||
      stringValue(refFields.urlPath) ||
      navigableRef
    )
  }
  return undefined
}

export const parseBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value
  return typeof value === 'string' && value.trim().toLowerCase() === 'true'
}

/** Normalize resolved and unresolved Contentful references without inventing fields. */
export const normalizeWorlds = (
  items: unknown,
  isMobile = false
): NormalizedWorld[] => {
  if (!Array.isArray(items)) return []

  return items
    .map((item: unknown, index): NormalizedWorld | null => {
      const itemRecord = asRecord(item)
      const fields = fieldsOf(itemRecord)
      const label = textOf(fields)
      const image = selectWorldImage(fields, isMobile)
      if (!label || !image) return null

      const referenceType = stringValue(fields.referenceType)?.toLowerCase()
      const sys = asRecord(itemRecord.sys)
      const fieldSys = asRecord(fields.sys)
      const stableId =
        stringValue(sys.id) || stringValue(fieldSys.id) || `${label}-${index}`

      return {
        id: stableId,
        label,
        image,
        href: resolveWorldLink(fields),
        referenceType,
        source: { ...fields }
      }
    })
    .filter((world): world is NormalizedWorld => {
      return world !== null
    })
    .slice(0, 20)
}

export const measureOverflow = (element: HTMLElement): boolean => {
  return element.scrollWidth > element.clientWidth
}

interface WorldCardProps {
  cfIndex?: number
  circleSize: number
  index: number
  isIntersecting: boolean
  isMobile: boolean
  isPriority?: boolean
  onClick: (world: NormalizedWorld, index: number) => void
  world: NormalizedWorld
}

const WorldCard = ({
  cfIndex,
  circleSize,
  index,
  isIntersecting,
  isMobile,
  isPriority,
  onClick,
  world
}: WorldCardProps) => {
  const cardClassName = isMobile
    ? `${styles.card} ${styles.cardMobile}`
    : styles.card
  const labelClassName = isMobile
    ? `${styles.label} ${styles.labelMobile}`
    : styles.label
  const content = (
    <span
      className={cardClassName}
      ref={(ref) => {
        if (ref && isIntersecting) {
          addToHomeData({
            ref,
            promoData: {
              creativeName: String(world.source.creativeName || world.label),
              creativeSlot: String(world.source.creativeSlot || index + 1),
              locationId: `${cfIndex} - ${index + 1}`,
              promotionId: String(world.source.promotionId || world.id),
              promotionName: String(world.source.promotionName || world.label)
            }
          })
        }
      }}
    >
      <Image
        alt={world.label}
        className={styles.image}
        height={circleSize}
        priority={isPriority && index === 0}
        src={world.image}
        style={{ height: circleSize, width: circleSize }}
        width={circleSize}
      />
      <span
        className={labelClassName}
        title={world.label}
      >
        {world.label}
      </span>
    </span>
  )

  return world.href ? (
    <a
      href={world.href}
      onClick={() => {
        return onClick(world, index)
      }}
    >
      {content}
    </a>
  ) : (
    <button
      aria-label={world.label}
      className={styles.worldButton}
      onClick={() => {
        return onClick(world, index)
      }}
      type='button'
    >
      {content}
    </button>
  )
}

interface WorldsContentProps {
  cfIndex?: number
  data: BannerHomeObj
  isMobile: boolean
  isIntersecting: boolean
  isPriority?: boolean
}

export const WorldsContent = ({
  cfIndex,
  data,
  isMobile,
  isIntersecting,
  isPriority
}: WorldsContentProps) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState<boolean | null>(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(true)
  const mobileLoopResetTimerRef = useRef<number | undefined>(undefined)
  const pendingLoopResetRef = useRef<{
    reset: number
    target: number
  } | null>(null)
  const worlds = normalizeWorlds(data.imageBanner, isMobile)
  const showViewAll = parseBoolean(data.viewAllButton)
  const isInfinite = parseBoolean(data.infinite)
  const viewAllHref = resolveWorldLink(data)
  const heightValue = Number(data.height)
  const hasCustomHeight = Number.isFinite(heightValue) && heightValue > 0
  // El alto configurado aplica solo al track de círculos (título y "Ver todos"
  // quedan fuera). Las burbujas conservan su tamaño de presentación para que
  // cambiar la altura no altere cuántos mundos caben en desktop.
  const naturalCircleSize = isMobile ? 100 : 128
  const circleSize = naturalCircleSize
  const trackHeight = hasCustomHeight ? heightValue : undefined
  const arrowMarginTop = circleSize / 2 - 16
  const title = stringValue(data.title) || ''
  const cardGap = isMobile ? 12 : 16
  const cardWidth = isMobile ? 112 : circleSize
  const cardStep = cardWidth + cardGap
  const loopStep = worlds.length * cardStep
  const isLooping = Boolean(isInfinite && isOverflowing)
  const getScrollBounds = (track: HTMLElement) => {
    const start = isLooping ? loopStep : 0
    const contentWidth = isLooping ? loopStep - cardGap : track.scrollWidth
    const max = Math.max(0, contentWidth - track.clientWidth)
    return { end: start + max, max, start }
  }
  const smoothScrollTo = (track: HTMLElement, left: number): boolean => {
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ behavior: 'smooth', left })
      return true
    }
    track.scrollLeft = left
    return false
  }

  const measure = () => {
    const track = trackRef.current
    if (!track) return
    const { end, start } = getScrollBounds(track)
    const contentWidth = isLooping ? loopStep - cardGap : track.scrollWidth
    setIsOverflowing(contentWidth > track.clientWidth)
    setIsAtStart(track.scrollLeft <= start + 1)
    setIsAtEnd(track.scrollLeft >= end - 1)
  }
  const handleScroll = () => {
    const track = trackRef.current
    if (!track) return

    const currentScrollLeft = track.scrollLeft
    const pendingLoopReset = pendingLoopResetRef.current
    if (pendingLoopReset !== null) {
      if (Math.abs(currentScrollLeft - pendingLoopReset.target) <= 1) {
        track.scrollLeft = pendingLoopReset.reset
        pendingLoopResetRef.current = null
      }
      measure()
      return
    }

    if (isLooping && isMobile) {
      window.clearTimeout(mobileLoopResetTimerRef.current)
      mobileLoopResetTimerRef.current = window.setTimeout(() => {
        const currentTrack = trackRef.current
        if (!currentTrack) return

        const { start } = getScrollBounds(currentTrack)
        const nextCopyStart = start + loopStep
        const settledScrollLeft = currentTrack.scrollLeft
        if (settledScrollLeft < start) {
          currentTrack.scrollLeft = settledScrollLeft + loopStep
        } else if (settledScrollLeft >= nextCopyStart) {
          currentTrack.scrollLeft = settledScrollLeft - loopStep
        }
        mobileLoopResetTimerRef.current = undefined
        measure()
      }, MOBILE_LOOP_IDLE_DELAY_MS)
    }
    measure()
  }

  const handleArrowClick = (direction: -1 | 1) => {
    const track = trackRef.current
    if (!track) return

    const { end, max, start } = getScrollBounds(track)
    const atStart = track.scrollLeft <= start + 1
    const atEnd = track.scrollLeft >= end - 1
    if (isLooping && (direction < 0 ? atStart : atEnd)) {
      const target = direction < 0 ? max : loopStep * 2
      const reset = direction < 0 ? start + max : start
      pendingLoopResetRef.current = { reset, target }
      smoothScrollTo(track, target)
      measure()
      return
    }
    if (!isLooping && isInfinite && (direction < 0 ? atStart : atEnd)) {
      const targetScrollLeft = direction < 0 ? max : start
      smoothScrollTo(track, targetScrollLeft)
      measure()
      return
    }

    const visibleCount = Math.max(
      1,
      Math.floor((track.clientWidth + cardGap) / cardStep)
    )
    const lastPageStart = Math.max(0, worlds.length - visibleCount)
    const pageStarts: number[] = []
    for (let pageStart = 0; ; pageStart += visibleCount) {
      const normalizedStart = Math.min(pageStart, lastPageStart)
      if (pageStarts[pageStarts.length - 1] !== normalizedStart) {
        pageStarts.push(normalizedStart)
      }
      if (normalizedStart === lastPageStart) break
    }

    const currentStart = Math.round((track.scrollLeft - start) / cardStep)
    let currentPageIndex = 0
    for (let index = 1; index < pageStarts.length; index += 1) {
      if (pageStarts[index] > currentStart) break
      currentPageIndex = index
    }
    const targetPageIndex = currentPageIndex + direction
    const targetStart = pageStarts[targetPageIndex]
    if (targetStart === undefined) return

    const targetScrollLeft = start + targetStart * cardStep
    track.scrollBy({
      behavior: 'smooth',
      left: targetScrollLeft - track.scrollLeft
    })
  }

  useEffect(() => {
    const track = trackRef.current
    if (!track || !isIntersecting) return undefined

    if (isLooping) {
      const { start } = getScrollBounds(track)
      if (track.scrollLeft < start - 1) track.scrollLeft = start
    } else if (isMobile || (isInfinite && !isMobile)) {
      track.scrollLeft = 0
    }
    measure()
    track.addEventListener('scroll', handleScroll)
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure)
      observer.observe(track)
      return () => {
        window.clearTimeout(mobileLoopResetTimerRef.current)
        observer.disconnect()
        track.removeEventListener('scroll', handleScroll)
      }
    }

    window.addEventListener('resize', measure)
    return () => {
      window.clearTimeout(mobileLoopResetTimerRef.current)
      track.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', measure)
    }
  }, [isInfinite, isIntersecting, isLooping, isMobile, loopStep, worlds.length])

  if (!isIntersecting || worlds.length === 0) return null

  const handleClick = (world: NormalizedWorld, index: number) => {
    clickedPromos({
      creativeName: String(world.source.creativeName || world.label),
      creativeSlot: String(world.source.creativeSlot || index + 1),
      promotionId: String(world.source.promotionId || world.id),
      promotionName: String(world.source.promotionName || world.label),
      locationId: `${cfIndex} - ${index + 1}`,
      urlPath: world.href
    })
  }

  const renderCards = (copy: number) => {
    return worlds.map((world, index) => {
      return (
        <WorldCard
          cfIndex={cfIndex}
          circleSize={circleSize}
          index={index}
          isIntersecting={isIntersecting && copy === 1}
          isMobile={isMobile}
          isPriority={isPriority && copy === 1}
          key={`${copy}-${world.id}`}
          onClick={handleClick}
          world={world}
        />
      )
    })
  }
  const cards = isLooping
    ? [...renderCards(0), ...renderCards(1), ...renderCards(2)]
    : renderCards(1)
  const trackClassName = isMobile
    ? `${styles.track} ${styles.trackMobile}`
    : `${styles.track} ${styles.trackDesktop}`
  const viewAll = showViewAll ? (
    viewAllHref ? (
      <a
        className={styles.viewAll}
        href={viewAllHref}
      >
        Ver todos
      </a>
    ) : (
      <button
        className={styles.viewAll}
        disabled
        type='button'
      >
        Ver todos
      </button>
    )
  ) : null

  return (
    <section
      aria-label={title || 'Tus Mundos'}
      className={styles.worlds}
    >
      <div className={styles.header}>
        {title ? (
          <h2
            aria-label={title}
            className={styles.title}
          >
            {title}
          </h2>
        ) : null}
        <div className={styles.headerViewAll}>{viewAll}</div>
      </div>
      {isMobile ? (
        <div
          className={trackClassName}
          data-testid='worlds-track'
          ref={trackRef}
          style={{
            height: trackHeight,
            justifyContent: isOverflowing ? 'flex-start' : 'center',
            overflowX: isOverflowing ? 'auto' : 'hidden',
            overflowY: hasCustomHeight ? 'hidden' : undefined
          }}
        >
          {cards}
        </div>
      ) : (
        <div className={styles.desktopControls}>
          {isOverflowing ? (
            <button
              aria-label='Previous worlds'
              className={styles.arrow}
              disabled={!isInfinite && isAtStart}
              onClick={() => {
                handleArrowClick(-1)
              }}
              style={{ marginTop: arrowMarginTop }}
              type='button'
            >
              ‹
            </button>
          ) : null}
          <div
            className={trackClassName}
            data-testid='worlds-track'
            ref={trackRef}
            style={{
              height: trackHeight,
              justifyContent: isOverflowing ? 'flex-start' : 'center',
              overflowX: isOverflowing ? 'auto' : 'hidden',
              overflowY: hasCustomHeight ? 'hidden' : undefined
            }}
          >
            {cards}
          </div>
          {isOverflowing ? (
            <button
              aria-label='Next worlds'
              className={styles.arrow}
              disabled={!isInfinite && isAtEnd}
              onClick={() => {
                handleArrowClick(1)
              }}
              style={{ marginTop: arrowMarginTop }}
              type='button'
            >
              ›
            </button>
          ) : null}
        </div>
      )}
    </section>
  )
}
